import type { AuthChangeEvent, Session } from '@supabase/supabase-js'
import { queryClient } from '@/lib/query-client'

/**
 * The one identity scope the query cache is allowed to hold data for.
 *
 * No query key in this app carries a tenant or user segment; the entire
 * tenant wall inside the cache is the identity boundary this module enforces.
 * This module owns those moments, from BOTH directions a session change can
 * arrive:
 *
 *  - auth events, via `use-auth.ts`'s `onAuthStateChange` listener
 *    (`observeAuthEvent`) — the PR #65 behavior, preserved exactly;
 *  - the `_authed` route guard, via `syncAuthCacheScope` — the boundary every
 *    authed navigation passes. This is what catches a session that changed
 *    WITHOUT an event being delivered to this tab: a cross-tab sign-in as
 *    another user/tenant whose broadcast was lost, and the redirect-to-/login
 *    path, where `throw redirect(...)` is a client-side navigation that tears
 *    nothing down.
 *
 * WHY A DOCUMENT RELOAD AND NOT ONLY `queryClient.clear()`. A clear empties
 * the cache at one instant; it cannot reach async work the OLD scope already
 * started. The concrete same-heap channel in THIS repo is the optimistic
 * rollback: every task mutation captures the pre-mutation list in `onMutate`
 * and its `onError` writes that captured list back with `setQueryData`
 * (use-tasks.ts — there is no success-side cache write; success paths only
 * invalidate). A tenant-A mutation that FAILS after the clear therefore
 * restores tenant-A rows into what is now tenant B's cache — proven against
 * the real `useCreateTask` in `e2e/auth-boundary-stackless.spec.ts` (the
 * clear-only CONTROL arm leaks; the document-boundary arm does not), and
 * pinned at unit level by the synthetic settlement plant in
 * `auth-cache-scope.test.ts`. The same reappearance path exists for any
 * captured closure: retries, subscriptions, timers. The only boundary that
 * terminates EVERY old-scope callback is tearing the document down — the
 * exact mechanism `forceSignOutToLogin` (auth-session.ts) and `signOut`
 * (use-auth.ts) already rely on. So a change of a PREVIOUSLY OBSERVED
 * readable identity escalates from clear to clear-then-reload; the clear
 * still runs first so nothing renders from the old scope during the teardown
 * race.
 *
 * WHEN THE RELOAD FIRES — exactly when the previously observed scope was a
 * READABLE signed-in identity and the next observation differs:
 *  - same user, changed/lost tenant claim  → reload (fresh boot as B)
 *  - different user (cross-tab replacement) → reload
 *  - session gone / SIGNED_OUT              → replace('/login')
 *  - readable → unreadable token            → reload (recorded scope is gone)
 * And when it deliberately does NOT fire:
 *  - first observation after boot (nothing cached, and reload-on-boot would
 *    loop);
 *  - previous scope null (sign-in from /login: the pre-sign-in heap holds no
 *    tenant data — sign-out itself already crossed a document boundary);
 *  - previous scope UNREADABLE: unreadable scopes compare unequal to
 *    everything (fail closed), so reloading on them would reload forever.
 *    Those observations keep the existing clear-per-observation behavior.
 *
 * Fail-closed rule: an unreadable token never counts as "same identity", so
 * two consecutive unreadable observations still clear. Only readable scopes
 * with equal (userId, tenantId), or two signed-out observations, preserve.
 */
export type AuthCacheScope = {
  tenantId: string | null
  tokenReadable: boolean
  userId: string
}

export function authCacheScope(session: Session | null): AuthCacheScope | null {
  if (!session) return null

  try {
    const payloadPart = session.access_token.split('.')[1]
    if (!payloadPart) throw new Error('access token has no payload')
    const base64 = payloadPart.replaceAll('-', '+').replaceAll('_', '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const payload = JSON.parse(atob(padded)) as { tenant_id?: unknown }

    return {
      tenantId:
        typeof payload.tenant_id === 'string' ? payload.tenant_id : null,
      tokenReadable: true,
      userId: session.user.id,
    }
  } catch {
    return {
      tenantId: null,
      tokenReadable: false,
      userId: session.user.id,
    }
  }
}

export function sameAuthCacheScope(
  current: AuthCacheScope | null,
  next: AuthCacheScope | null,
) {
  if (!current || !next || !current.tokenReadable || !next.tokenReadable) {
    return current === null && next === null
  }

  return current.userId === next.userId && current.tenantId === next.tenantId
}

/**
 * The document boundary, as a seam. Production behavior is
 * `window.location`; unit tests replace the members to observe WHICH
 * boundary fired without jsdom attempting real navigation. The seam carries
 * no logic — the decision of when to cross it stays in this module and is
 * what the tests pin.
 */
export const documentBoundary = {
  /** Sign-out destination. Full load: the heap, and with it every pending
   *  old-scope callback, is destroyed. Skipped when already on /login (the
   *  post-boundary page — navigating it to itself would loop). Recorded
   *  residual: a SIGNED_OUT observed while the document is STILL /login
   *  (sign-in observed, cross-tab sign-out before the post-login navigation)
   *  keeps that heap; it holds no tenant data at that point, and the next
   *  identity change from a readable scope crosses the boundary normally. */
  replaceToLogin(): void {
    const { pathname } = window.location
    if (pathname === '/login' || pathname.startsWith('/login/')) return
    window.location.replace('/login')
  },
  /** Identity-change destination: boot the current URL again under the new
   *  identity. Route guards re-run against the fresh session on load. */
  reload(): void {
    window.location.reload()
  },
}

// One cache, one scope: module-level on purpose. Both observation paths below
// compare against — and advance — the same record, so the guard and the event
// listener can never disagree about what identity the cache belongs to.
let activeScope: AuthCacheScope | null = null
let scopeObserved = false

/**
 * Clears, then — when the identity that was previously observed was a
 * readable signed-in scope — crosses the document boundary. See the module
 * doc for the exact matrix. The clear ALWAYS runs first: `location.replace`
 * and `location.reload` are asynchronous teardowns, and nothing from the old
 * scope may render or be read in the interim.
 */
function evictOldScope(nextScope: AuthCacheScope | null): void {
  queryClient.clear()
  if (!scopeObserved || activeScope === null || !activeScope.tokenReadable) {
    return
  }
  if (nextScope === null) documentBoundary.replaceToLogin()
  else documentBoundary.reload()
}

/**
 * Auth-event path (PR #65 semantics plus one review correction): SIGNED_OUT
 * always clears; any other event clears when the identity changed — INCLUDING
 * `INITIAL_SESSION`, once a scope has been observed. `INITIAL_SESSION` is not
 * a boot-only event: auth-js emits it to EACH new `onAuthStateChange`
 * subscription with the then-current session, so a late `useAuth` mount can
 * deliver a changed identity through it (cross-tab re-login whose broadcast
 * was lost). Only the FIRST observation is exempt, where the in-memory cache
 * is necessarily empty — there is nothing to clear and no persistence exists
 * (verified: no persistQueryClient/storage persister anywhere).
 */
export function observeAuthEvent(
  event: AuthChangeEvent,
  session: Session | null,
): void {
  const nextScope = authCacheScope(session)
  const identityChanged = !sameAuthCacheScope(activeScope, nextScope)

  if (
    event === 'SIGNED_OUT' ||
    (identityChanged && (event !== 'INITIAL_SESSION' || scopeObserved))
  ) {
    evictOldScope(nextScope)
  }

  activeScope = nextScope
  scopeObserved = true
}

/**
 * Navigation-boundary path: evicts when a previously observed identity scope
 * changed, including to null (session gone → the guard is about to redirect
 * to /login; the document boundary makes that a FULL load so nothing pending
 * survives it). The first observation after boot only records — the cache
 * cannot hold anything yet.
 */
export function syncAuthCacheScope(session: Session | null): void {
  const nextScope = authCacheScope(session)

  if (scopeObserved && !sameAuthCacheScope(activeScope, nextScope)) {
    evictOldScope(nextScope)
  }

  activeScope = nextScope
  scopeObserved = true
}
